import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  addApkGooglePlayStoreChannelValidationSchema,
  TAddApkGooglePlayStoreChannelValidation,
  useAddApkGooglePlayStoreChannel,
} from '../../../api/add-apk-google-play-store-channel.api';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { GET_LIST_GOOGLE_PLAY_STORE_CHANNEL_QUERY_KEY } from '../../../api/get-list-google-play-store-channel.api';
import { queryClient } from '@/lib/react-query';
import { handleDomBubbleEvent } from '@/utils/dom';
import { cn } from '@/utils/cn';
import { Loader2, Plus, X } from 'lucide-react';
import Input from '@/components/ui/input';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalConfirmButton,
} from '@/components/_ui/modal';

const TOAST_MESSAGES_ADD_GOOGLE_PLAY_STORE_APK = {
  success: 'Google play store apk added successfully',
  error: 'Failed to add google play store apk',
};

export type TAddGooglePlayStoreApkModalType = 'AddGooglePlayStoreApkModal';
export interface IAddGooglePlayStoreApkModalProps {
  state: string;
}

const AddGooglePlayStoreApkModal = memo(({ state }: IAddGooglePlayStoreApkModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const navigate = useNavigate();
  const [currentApkName, setCurrentApkName] = useState('');
  const channelModalRootActions = useChannelModalRootActionsStore();
  const form = useForm<TAddApkGooglePlayStoreChannelValidation>({
    mode: 'onChange',
    resolver: zodResolver(addApkGooglePlayStoreChannelValidationSchema),
  });

  const mutateAddApkGooglePlayStoreChannel = useAddApkGooglePlayStoreChannel({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_ADD_GOOGLE_PLAY_STORE_APK.error;

        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [GET_LIST_GOOGLE_PLAY_STORE_CHANNEL_QUERY_KEY],
          }),
        ]);
        channelModalRootActions.closeModal('AddGooglePlayStoreApkModal');
        navigate('/channel-integrations/google-play-store');
        showToast({
          message: TOAST_MESSAGES_ADD_GOOGLE_PLAY_STORE_APK.success,
          type: 'success',
        });
      },
    },
  });

  // HELPERS
  const isDisabledButton = useMemo(() => {
    return mutateAddApkGooglePlayStoreChannel.isPending || form.watch('names')?.length === 0;
  }, [mutateAddApkGooglePlayStoreChannel.isPending, form.watch('names')]);

  // EFFECTS
  useEffect(() => {
    if (state) {
      form.reset({
        state,
        names: [],
      });
    }
  }, [state, form]);

  // HANDLERS
  const onSubmit = useCallback(
    (data: TAddApkGooglePlayStoreChannelValidation) => {
      if (!data.state || data?.names?.length === 0) {
        showToast({
          type: 'error',
          message: 'Please select a state and add at least one APK name!',
        });
        return;
      }
      mutateAddApkGooglePlayStoreChannel.mutate({
        state: data.state,
        names: data.names,
      });
    },
    [mutateAddApkGooglePlayStoreChannel, showToast]
  );

  const handleAddApkName = useCallback(() => {
    if (!currentApkName.trim()) {
      showToast({
        type: 'error',
        message: 'Please enter an APK name',
      });
      return;
    }

    const currentNames = form.watch('names') || [];
    if (currentNames.includes(currentApkName.trim())) {
      showToast({
        type: 'error',
        message: 'This APK name already exists',
      });
      return;
    }

    const newNames = [...currentNames, currentApkName.trim()];
    form.setValue('names', newNames);
    setCurrentApkName('');
  }, [currentApkName, form, showToast]);

  const handleRemoveApkName = useCallback(
    (nameToRemove: string) => {
      const currentNames = form.watch('names') || [];
      const newNames = currentNames.filter((name) => name !== nameToRemove);
      form.setValue('names', newNames);
    },
    [form]
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        handleAddApkName();
      }
    },
    [handleAddApkName]
  );

  const handleCloseModal = useCallback(() => {
    channelModalRootActions.closeModal('AddGooglePlayStoreApkModal');
  }, [channelModalRootActions]);

  const handleRemoveAllnames = useCallback(() => {
    form.setValue('names', []);
  }, [form]);

  return (
    <Modal
      isOpen={channelModalRootActions.checkIsOpenModal('AddGooglePlayStoreApkModal')}
      onClose={handleCloseModal}
      isCloseOutside={false}
      lockBodyScroll
      className={cn('xl:w-[500px] w-[90vw] p-[32px]')}
    >
      <ModalHeader>
        <ModalTitle>Please Insert your Play Store Information</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(onSubmit)(e);
          }}
          id="add-google-play-store-apk-form"
          className="gap-[12px] h-[300px] flex flex-col min-h-0"
        >
          <div className="flex flex-col gap-2">
            <label className="text-[14px] font-normal text-[#25151E]">APK Name</label>
            <div className="flex gap-2">
              <Input
                id="apk-name"
                placeholder="APK Name (e.g., com.example.app)"
                value={currentApkName}
                onChange={(e) => setCurrentApkName(e.target.value)}
                onKeyDown={handleKeyPress}
                className="flex-1"
              />
              <button
                type="button"
                onClick={handleAddApkName}
                className="px-4 py-2 bg-[#0C8DBF] text-white rounded-[8px] text-[14px] font-normal hover:bg-[#0a7ba8] flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
            {form.formState.errors.names && (
              <div className="text-[12px] font-normal text-[#CC0202]">
                <span className="label-text-alt text-error">{form.formState.errors.names?.message}</span>
              </div>
            )}
          </div>

          {/* APK Names List */}
          <div className="flex-1 flex flex-col min-h-0 gap-[4px]">
            {form.watch('names')?.length > 0 && (
              <div className="flex items-center justify-between mb-[4px]">
                <span className="text-[14px] font-medium text-[#25151E]">
                  Added APKs ({form.watch('names')?.length || 0})
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveAllnames()}
                  className="text-[#CC0202] flex items-center gap-1 hover:text-[#a00000] px-[5px] py-[3px] bg-[#fef2f2] text-[12px] font-normal rounded transition-colors"
                  title="Remove all APK names"
                >
                  Remove All <X className="w-4 h-4 flex-shrink-0" />
                </button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto border border-[#D9D9D9] rounded-[8px] min-h-0 p-2">
              {form.watch('names')?.length === 0 ? (
                <div className="text-center text-[#9D9D9C] text-[14px] py-4">No APK names added yet</div>
              ) : (
                <div className="space-y-2">
                  {form.watch('names')?.map((apkName, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-[#F8F9FA] rounded-[6px] px-3 py-2 border border-[#E9ECEF]"
                    >
                      <span className="text-[14px] font-normal text-[#25151E] flex-1">{apkName}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveApkName(apkName)}
                        className="text-[#CC0202] hover:text-[#a00000] p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleCloseModal();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="add-google-play-store-apk-form" type="submit" disabled={isDisabledButton}>
          {mutateAddApkGooglePlayStoreChannel.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Connect
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

AddGooglePlayStoreApkModal.displayName = 'AddGooglePlayStoreApkModal';
export default AddGooglePlayStoreApkModal;
